import React, { useState } from 'react';
import { Button } from '@shadcn/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@shadcn/components/ui/dropdown-menu';
import { exportToExcel } from './ExcelExportUtils';
import { transformDataForExport } from './DataTransformer';

const ExportButton = ({
  data,
  forecastData,
  selectedRegion,
  selectedMonths,
  viewType,
  filters = {}
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (exportType) => {
    setIsExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for UX
      
      // Transform data to the correct format for export
      let exportData;
      if (exportType === viewType) {
        // If exporting in current view format, use filtered data
        exportData = data;
      } else {
        // If exporting in different format, transform from raw data
        if (!forecastData || !selectedRegion) {
          throw new Error('Raw forecast data not available');
        }
        exportData = transformDataForExport(forecastData, selectedRegion, selectedMonths, exportType, filters);
      }
      
      exportToExcel(exportData, selectedMonths, exportType, filters);
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message || 'Please try again.'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const getExportLabel = (type) => {
    let count;
    if (type === viewType) {
      count = data.length;
    } else {
      // Calculate count for different view type
      if (!forecastData || !selectedRegion) {
        count = 0;
      } else {
        const dataSource = forecastData[type === 'category' ? 'byCategory' : type === 'product' ? 'byProduct' : 'bySku']?.[selectedRegion] || {};
        count = Object.keys(dataSource).length;
      }
    }
    
    switch (type) {
      case 'category':
        return `Export Categories (${count} items)`;
      case 'product':
        return `Export Products (${count} items)`;
      case 'sku':
        return `Export SKUs (${count} items)`;
      default:
        return `Export (${count} items)`;
    }
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'category':
        return '📂';
      case 'product':
        return '📦';
      case 'sku':
        return '🏷️';
      default:
        return '📊';
    }
  };

  if (!data || data.length === 0) {
    return (
      <Button variant="outline" disabled>
        
        Export
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          disabled={isExporting}
          className="flex items-center gap-2"
        >
          
          {isExporting ? 'Exporting...' : 'Export'}
          <span className="text-xs ml-1">▼</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="p-2 text-xs text-gray-500 border-b">
          Choose export format:
        </div>
        
        {/* Current view type - highlighted */}
        <DropdownMenuItem
          onClick={() => handleExport(viewType)}
          className="flex items-center gap-3 py-3 bg-blue-50 border-l-2 border-blue-500"
        >
        
          <div className="flex-1">
            <div className="font-medium text-blue-700">
              {getExportLabel(viewType)}
            </div>
            
          </div>
        </DropdownMenuItem>

        {/* Other export options */}
        {viewType !== 'category' && (
          <DropdownMenuItem
            onClick={() => handleExport('category')}
            className="flex items-center gap-3 py-3"
          >
            
            <div className="flex-1">
              <div className="font-medium">Export by Category</div>
              
            </div>
          </DropdownMenuItem>
        )}

        {viewType !== 'product' && (
          <DropdownMenuItem
            onClick={() => handleExport('product')}
            className="flex items-center gap-3 py-3"
          >
           
            <div className="flex-1">
              <div className="font-medium">Export by Product</div>
              
            </div>
          </DropdownMenuItem>
        )}

        {viewType !== 'sku' && (
          <DropdownMenuItem
            onClick={() => handleExport('sku')}
            className="flex items-center gap-3 py-3"
          >
            
            <div className="flex-1">
              <div className="font-medium">Export by SKU</div>
              
            </div>
          </DropdownMenuItem>
        )}

        
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton; 
